import { Button, ErrorAlert, Notice } from '@lumlab/platform-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AuthScreen, takeFragmentParameters } from '../app/AuthScreen.tsx';

/**
 * Unsubscription from a category of messages (tender digests…), without a session. The link of the message opens
 * this page; only the button unsubscribes, because link scanners open every URL of a message.
 */
export function UnsubscribePage() {
    const { t } = useTranslation();
    const [token] = useState(() => takeFragmentParameters().get('token') ?? '');
    const [state, setState] = useState<'idle' | 'pending' | 'done'>('idle');
    const [error, setError] = useState<{ code: string } | null>(null);

    const confirm = async () => {
        setState('pending');
        setError(null);
        try {
            const response = await fetch('/api/public/mail/unsubscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token }),
            });
            if (response.ok) return setState('done');
            setError({
                code: response.status === 404 ? 'invalid_link' : response.status === 429 ? 'rate_limited' : 'internal',
            });
        } catch {
            setError({ code: 'network' });
        }
        setState('idle');
    };

    return (
        <AuthScreen title={t('platform.unsubscribe.title')}>
            {!token ? (
                <Notice tone="warning">{t('platform.unsubscribe.missingToken')}</Notice>
            ) : state === 'done' ? (
                <Notice tone="success">{t('platform.unsubscribe.done')}</Notice>
            ) : (
                <>
                    <p className="text-sm text-muted">{t('platform.unsubscribe.intro')}</p>
                    {error && <ErrorAlert error={error} />}
                    <Button loading={state === 'pending'} onClick={() => void confirm()}>
                        {t('platform.unsubscribe.submit')}
                    </Button>
                </>
            )}
        </AuthScreen>
    );
}
